/**
 * Qualification Check Utility
 *
 * Single source of truth for determining if an annotator qualifies
 * for a job. Used by:
 *   - Jobs.jsx (canQuickApply, canApply, detail panel)
 *   - AppContext.jsx (startJob guard, applyToJob step tracking)
 *
 * Checks: domain assessment, skills evaluation, resume, work auth,
 *         assessment expiration, special assessment requirements.
 */

/**
 * Check if an assessment result is still valid (not expired).
 * @param {Object} assessment - transformed assessment with expiresAt field
 * @returns {boolean}
 */
export function isAssessmentValid(assessment) {
  if (!assessment) return false;
  if (assessment.status !== 'passed') return false;
  // No expiration date = never expires
  if (!assessment.expiresAt) return true;
  return new Date(assessment.expiresAt) > new Date();
}

/**
 * Get all domains the user has valid (passed + not expired) domain assessments for.
 * @param {Array} assessments - transformed assessment array from jobAdapter
 * @returns {Set<string>} set of domain names
 */
export function getQualifiedDomains(assessments) {
  if (!assessments || !assessments.length) return new Set();

  return new Set(
    assessments
      .filter(a => a.type === 'domain' && isAssessmentValid(a))
      .map(a => a.domain)
      .filter(Boolean)
  );
}

/**
 * Get all domains the user has valid skills evaluations for.
 * @param {Array} assessments
 * @returns {Set<string>}
 */
export function getSkillsEvalDomains(assessments) {
  if (!assessments || !assessments.length) return new Set();

  return new Set(
    assessments
      .filter(a => (a.type === 'skills' || a.type === 'Skills') && isAssessmentValid(a))
      .map(a => a.domain)
      .filter(Boolean)
  );
}

/**
 * Check if user has valid work authorization.
 * @param {Array} assessments - may contain compliance type
 * @param {Object} user - individual record with work_auth_status
 * @returns {boolean}
 */
export function hasValidWorkAuth(assessments, user) {
  // Check individual table field first
  const statusFromProfile = user?.work_auth_status || user?.workAuthStatus;
  if (statusFromProfile === 'Verified') return true;

  // Fall back to compliance assessment
  if (!assessments) return false;
  return assessments.some(a =>
    (a.type === 'Compliance' || a.type === 'compliance') && isAssessmentValid(a)
  );
}

/**
 * Check if user has a resume uploaded.
 * @param {Object} user - individual record
 * @returns {boolean}
 */
export function hasResume(user) {
  return !!(user?.resume_url || user?.resumeUrl);
}

/**
 * Check if user has passed all required assessments for a job.
 * Used when job has specific assessment requirements from job_required_assessment table.
 *
 * @param {Array} requiredAssessments - [{ assessmentDefinitionId, isRequired }] from junction table
 * @param {Array} userAssessments - transformed assessment array with status/type/domain/expiresAt
 * @returns {Object} { allMet, missing: [assessmentId, ...] }
 */
export function checkRequiredAssessments(requiredAssessments, userAssessments) {
  if (!requiredAssessments || requiredAssessments.length === 0) {
    return { allMet: true, missing: [] };
  }

  const passedIds = new Set(
    (userAssessments || [])
      .filter(a => isAssessmentValid(a) && a.assessmentId)
      .map(a => a.assessmentId)
  );

  const missing = requiredAssessments
    .filter(ra => ra.isRequired !== false) // skip non-required (recommended)
    .filter(ra => !passedIds.has(ra.assessmentDefinitionId))
    .map(ra => ra.assessmentDefinitionId);

  return { allMet: missing.length === 0, missing };
}

/**
 * Full qualification check for a specific job.
 * Returns detailed result with what's met and what's missing.
 *
 * @param {Object} params
 * @param {Object} params.job - transformed job object
 * @param {Object} params.user - individual/currentUser record
 * @param {Array} params.assessments - transformed assessment array
 * @param {Array} [params.requiredAssessments] - from job_required_assessment junction table
 * @returns {Object} qualification result
 */
export function checkQualifications({ job, user, assessments, requiredAssessments }) {
  if (!job || !user) {
    return { qualified: false, canQuickApply: false, canStartNow: false, missing: ['No job or user data'], steps: {} };
  }

  const qualifiedDomains = getQualifiedDomains(assessments);
  const skillsDomains = getSkillsEvalDomains(assessments);

  // Step checks
  const steps = {
    resume: hasResume(user),
    domainAssessment: qualifiedDomains.has(job.domain),
    skillsEvaluation: skillsDomains.has(job.domain),
    workAuth: hasValidWorkAuth(assessments, user),
  };

  // Special assessment override: if job needs special assessment,
  // domain + skills assessments don't count (need job-specific ones)
  if (job.needsSpecialAssessment) {
    steps.domainAssessment = false;
    steps.skillsEvaluation = false;
  }

  // Check job-specific required assessments (from junction table)
  const reqCheck = checkRequiredAssessments(requiredAssessments, assessments);
  steps.requiredAssessments = reqCheck.allMet;

  // What's missing
  const missing = [];
  if (!steps.resume) missing.push('Resume');
  if (!steps.domainAssessment) missing.push('Domain Assessment');
  if (!steps.skillsEvaluation) missing.push('Skills Evaluation');
  if (!steps.workAuth) missing.push('Work Authorization');
  if (!steps.requiredAssessments) missing.push(`Required Assessments (${reqCheck.missing.length})`);

  // Core 4 steps (resume, domain, skills, workAuth) — required assessments are bonus validation
  const coreComplete = steps.resume && steps.domainAssessment && steps.skillsEvaluation && steps.workAuth;
  const completedCount = [steps.resume, steps.domainAssessment, steps.skillsEvaluation, steps.workAuth].filter(Boolean).length;
  const allComplete = coreComplete && steps.requiredAssessments;

  // canQuickApply: domain assessment passed for this job's domain + no special assessment
  // This is the minimum bar for showing Quick Apply / Start Now button
  const canQuickApply = steps.domainAssessment && !job.needsSpecialAssessment;

  // canStartNow: ALL qualifications met + required assessments + job has instant match enabled
  // This is the bar for bypassing the application pipeline entirely
  const canStartNow = allComplete && (job.instantMatch || job.instant_match_enabled) && !job.needsSpecialAssessment;

  return {
    qualified: allComplete,
    canQuickApply,
    canStartNow,
    missing,
    steps,
    completedCount,
    requiredAssessmentsMissing: reqCheck.missing,
  };
}
